import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import HubPageShell from "../components/layout/HubPageShell";
import IntegrationSupportChatPanel from "../components/integration-support/IntegrationSupportChatPanel";
import { useIntegrationSupportAgentChat } from "../hooks/useIntegrationSupportAgentChat";
import { useSubscriptions } from "../hooks/useSubscriptions";
import { useBookmarkableTab } from "../hooks/useBookmarkableTab";
import {
  isIntegrationHandoffConsumed,
  loadIntegrationHandoffPayload,
  markIntegrationHandoffConsumed,
} from "../lib/journeyHandoff";
import { apiService } from "../api/services/apiService";
import {
  buildCertificationMessage,
  buildCodeReviewMessage,
  buildProductFocusMessage,
  readCodeFile,
} from "../utils/codeFileReader";
import {
  overallStatusLabel,
  parseIntegrationCertificationChecklist,
  parseIntegrationCodeReview,
  parseIntegrationCodeSamples,
  readinessLabel,
  severityColor,
} from "../utils/integrationSupportOutput";
import type { EnrolledProduct } from "../utils/subscriptionProducts";
import { getTenantId } from "../utils/jwtUtils";
import { HUB_CHAT_PANEL_HEIGHT } from "../utils/hubLayout";

const TAB_SLUGS = ["integrate", "products", "review", "certify", "feedback"] as const;

const QUICK_PROMPTS = [
  "What products am I subscribed to and where should I start?",
  "Generate a curl example for authenticating with my product",
  "I'm getting a 401 — help me troubleshoot",
];

const CODE_LANGUAGES = [
  "javascript",
  "typescript",
  "python",
  "java",
  "go",
  "json",
  "yaml",
  "shell",
  "text",
];

const FEEDBACK_CATEGORIES = [
  "General feedback",
  "Incorrect guidance",
  "Missing documentation",
  "Code review quality",
  "Certification checklist",
  "Feature request",
];

const IntegrationSupportHubPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tabIndex, setTabIndex } = useBookmarkableTab({ slugs: TAB_SLUGS });
  const chat = useIntegrationSupportAgentChat();
  const { approvedProducts, loading: subsLoading, error: subsError } = useSubscriptions();
  const { sessionReady, sendMessage } = chat;

  const [selectedEnrollment, setSelectedEnrollment] = useState<EnrolledProduct | null>(null);
  const handoffProcessedRef = useRef(false);

  const [codeText, setCodeText] = useState("");
  const [codeLanguage, setCodeLanguage] = useState("javascript");
  const [codeFileError, setCodeFileError] = useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [certifyLoading, setCertifyLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [feedbackRating, setFeedbackRating] = useState<number | null>(4);
  const [feedbackCategory, setFeedbackCategory] = useState(FEEDBACK_CATEGORIES[0]);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [includeTranscript, setIncludeTranscript] = useState(true);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const goToTabSlug = (slug: (typeof TAB_SLUGS)[number]) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", slug);
        return next;
      },
      { replace: true },
    );
  };

  const lastAssistant = useMemo(() => {
    const assistants = chat.messages.filter((m) => m.role === "assistant");
    return assistants[assistants.length - 1]?.content ?? "";
  }, [chat.messages]);

  const codeReview = useMemo(
    () => parseIntegrationCodeReview(lastAssistant),
    [lastAssistant],
  );

  const certification = useMemo(
    () => parseIntegrationCertificationChecklist(lastAssistant),
    [lastAssistant],
  );

  const codeSamples = useMemo(
    () =>
      parseIntegrationCodeSamples(lastAssistant).filter((sample) =>
        sample.code?.trim(),
      ),
    [lastAssistant],
  );

  const hasAssistantResponse = useMemo(
    () =>
      chat.messages.some((m) => m.role === "assistant" && m.content.trim().length > 0),
    [chat.messages],
  );

  const integrateSidebarMode = useMemo(() => {
    if (codeReview || codeSamples.length > 0) return "results" as const;
    if (!hasAssistantResponse) return "context" as const;
    return "hidden" as const;
  }, [codeReview, codeSamples.length, hasAssistantResponse]);

  const activeSubscriptions = approvedProducts;

  useEffect(() => {
    const handoffId = searchParams.get("handoffId");
    const productUuidParam = searchParams.get("productUuid");
    if (!handoffId || handoffProcessedRef.current) return;
    if (!sessionReady || subsLoading) return;
    if (isIntegrationHandoffConsumed(handoffId)) {
      handoffProcessedRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("handoffId");
          return next;
        },
        { replace: true },
      );
      return;
    }

    const payload = loadIntegrationHandoffPayload(handoffId);
    if (!payload?.initialPrompt) return;

    handoffProcessedRef.current = true;

    const targetUuid = payload.productUuid ?? productUuidParam;
    if (targetUuid) {
      const match = activeSubscriptions.find(
        (entry) => entry.product.uuid === targetUuid,
      );
      if (match) {
        setSelectedEnrollment(match);
      }
    }

    void sendMessage(payload.initialPrompt).finally(() => {
      markIntegrationHandoffConsumed(handoffId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("handoffId");
          return next;
        },
        { replace: true },
      );
    });
  }, [
    searchParams,
    sessionReady,
    subsLoading,
    activeSubscriptions,
    sendMessage,
    setSearchParams,
  ]);

  const handleStartIntegration = useCallback(
    async (enrollment: EnrolledProduct) => {
      setSelectedEnrollment(enrollment);
      goToTabSlug("integrate");
      const { product } = enrollment;
      if (!product?.uuid) return;
      await chat.sendMessage(buildProductFocusMessage(product.uuid, product.name));
    },
    [chat],
  );

  const handleCodeFile = async (file: File) => {
    setCodeFileError(null);
    try {
      const result = await readCodeFile(file);
      setCodeText(result.text);
      setCodeLanguage(result.language);
    } catch (err: unknown) {
      setCodeFileError(err instanceof Error ? err.message : "Failed to read file");
    }
  };

  const handleSubmitCodeReview = async () => {
    if (!codeText.trim()) {
      setCodeFileError("Paste or upload code to review");
      return;
    }
    setCodeFileError(null);
    setReviewLoading(true);
    goToTabSlug("integrate");
    try {
      await chat.sendMessage(
        buildCodeReviewMessage({
          fileName: "pasted-code",
          language: codeLanguage,
          text: codeText,
          productUuid: selectedEnrollment?.product?.uuid ?? null,
        }),
      );
    } finally {
      setReviewLoading(false);
    }
  };

  const handleRunCertification = async () => {
    if (!selectedEnrollment?.product?.uuid) {
      setCodeFileError("Select a product from My products first");
      goToTabSlug("products");
      return;
    }
    setCertifyLoading(true);
    goToTabSlug("integrate");
    try {
      await chat.sendMessage(
        buildCertificationMessage(
          selectedEnrollment.product.uuid,
          selectedEnrollment.product.name,
        ),
      );
    } finally {
      setCertifyLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackComment.trim()) {
      setFeedbackError("Please enter your feedback");
      return;
    }
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    setFeedbackSuccess(null);
    try {
      const tenantId = getTenantId();
      const transcript =
        includeTranscript && chat.messages.length > 1
          ? chat.messages
              .slice(-6)
              .map((m) => `[${m.role}] ${m.content.slice(0, 500)}`)
              .join("\n\n---\n\n")
          : "";
      const description = [
        `Rating: ${feedbackRating ?? "n/a"}/5`,
        `Category: ${feedbackCategory}`,
        "",
        feedbackComment.trim(),
        transcript ? `\n--- Recent chat excerpt ---\n${transcript}` : "",
        chat.sessionId ? `\nAgent session: ${chat.sessionId}` : "",
      ].join("\n");

      const tags = ["integration-agent-feedback"];
      if (chat.sessionId) tags.push(`agent-session:${chat.sessionId}`);

      await apiService.createSupportCase({
        tenantId,
        name: `Integration Agent feedback: ${feedbackCategory}`,
        description,
        productUuid: selectedEnrollment?.product?.uuid,
        tags,
        entityClass: "integration-agent-feedback",
      });
      setFeedbackSuccess("Thank you — your feedback was submitted as a support case.");
      setFeedbackComment("");
    } catch (err: unknown) {
      setFeedbackError(err instanceof Error ? err.message : "Failed to submit feedback");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text);
  };

  return (
    <HubPageShell
      pageTitle="Integration Support Analyst"
      pageDescription="Guide partners through API integration, troubleshooting, code review, and certification readiness."
    >
      <Button
        component={RouterLink}
        to="/agent-dashboard"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: "none" }}
      >
        Agents
      </Button>

      {selectedEnrollment?.product && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Focus product: <strong>{selectedEnrollment.product.name}</strong>
          {" — "}
          <Button size="small" onClick={() => setSelectedEnrollment(null)}>
            Clear
          </Button>
        </Alert>
      )}

      <Tabs
        value={tabIndex}
        onChange={setTabIndex}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Integrate" />
        <Tab label="My products" />
        <Tab label="Review code" />
        <Tab label="Certify" />
        <Tab label="Feedback" />
      </Tabs>

      {tabIndex === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={integrateSidebarMode === "hidden" ? 12 : 7}>
            <IntegrationSupportChatPanel
              messages={chat.messages}
              onSend={(text) => void chat.sendMessage(text)}
              loading={chat.loading}
              error={chat.error}
              contextUsage={chat.contextUsage}
              quickPrompts={QUICK_PROMPTS}
              height={HUB_CHAT_PANEL_HEIGHT}
            />
          </Grid>
          {integrateSidebarMode !== "hidden" && (
          <Grid item xs={12} md={5}>
            {integrateSidebarMode === "results" ? (
              <>
                {codeReview && (
                <Paper variant="outlined" sx={{ p: 2, mb: codeSamples.length > 0 ? 2 : 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Code review</Typography>
                    <Chip
                      size="small"
                      label={overallStatusLabel(codeReview.overallStatus)}
                      color={
                        codeReview.overallStatus === "ready"
                          ? "success"
                          : codeReview.overallStatus === "blocked"
                            ? "error"
                            : "warning"
                      }
                    />
                  </Stack>
                  {codeReview.summary && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {codeReview.summary}
                    </Typography>
                  )}
                  <Stack spacing={1}>
                    {codeReview.findings.map((f, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                          <Chip size="small" label={f.severity} color={severityColor(f.severity)} />
                          <Chip size="small" variant="outlined" label={f.area} />
                        </Stack>
                        <Typography variant="body2" fontWeight={600}>
                          {f.issue}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {f.suggestion}
                        </Typography>
                        {f.optionalFixedSnippet && (
                          <Box sx={{ mt: 1, position: "relative" }}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              value={f.optionalFixedSnippet}
                              InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
                            />
                            <Button
                              size="small"
                              startIcon={<ContentCopyIcon />}
                              onClick={() => {
                                copyToClipboard(f.optionalFixedSnippet ?? "");
                                setCodeText(f.optionalFixedSnippet ?? "");
                              }}
                              sx={{ mt: 0.5 }}
                            >
                              Copy snippet
                            </Button>
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
                )}
                {codeSamples.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Generated code samples
                  </Typography>
                  <Stack spacing={1}>
                    {codeSamples.map((sample, i) => (
                      <Box key={i}>
                        <Typography variant="caption" color="text.secondary">
                          {sample.label ?? sample.language}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          value={sample.code}
                          InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
                        />
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => copyToClipboard(sample.code)}
                        >
                          Copy
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
                )}
              </>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, position: "sticky", top: 24 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Integration context
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Start with a quick prompt or pick a subscribed product to focus the session.
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {QUICK_PROMPTS.map((prompt) => (
                    <Button
                      key={prompt}
                      size="small"
                      variant="outlined"
                      sx={{ justifyContent: "flex-start", textAlign: "left" }}
                      onClick={() => void chat.sendMessage(prompt)}
                    >
                      {prompt}
                    </Button>
                  ))}
                </Stack>
                {subsLoading ? (
                  <CircularProgress size={20} />
                ) : activeSubscriptions.length > 0 ? (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      Your subscriptions
                    </Typography>
                    {activeSubscriptions.slice(0, 4).map((enrollment) => (
                      <Button
                        key={`${enrollment.subscription.uuid}:${enrollment.product.uuid}`}
                        size="small"
                        variant={
                          selectedEnrollment?.product.uuid === enrollment.product.uuid &&
                          selectedEnrollment?.subscription.uuid === enrollment.subscription.uuid
                            ? "contained"
                            : "text"
                        }
                        sx={{ justifyContent: "flex-start" }}
                        onClick={() => void handleStartIntegration(enrollment)}
                      >
                        {enrollment.product.name}
                      </Button>
                    ))}
                    <Button size="small" onClick={() => goToTabSlug("products")}>
                      View all products
                    </Button>
                  </Stack>
                ) : (
                  <Button component={RouterLink} to="/add-subscription" size="small" variant="contained">
                    Add subscription
                  </Button>
                )}
              </Paper>
            )}
          </Grid>
          )}
        </Grid>
      )}

      {tabIndex === 1 && (
        <Box>
          {subsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {subsError && <Alert severity="error">{subsError}</Alert>}
          {!subsLoading && activeSubscriptions.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                You have no active product subscriptions yet.
              </Typography>
              <Button component={RouterLink} to="/add-subscription" variant="contained">
                Add subscription
              </Button>
            </Paper>
          )}
          <Grid container spacing={2}>
            {activeSubscriptions.map((enrollment) => (
              <Grid item xs={12} sm={6} md={4} key={`${enrollment.subscription.uuid}:${enrollment.product.uuid}`}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{enrollment.product.name}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {enrollment.subscription.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {enrollment.product.description?.slice(0, 120) ??
                        "No description"}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => void handleStartIntegration(enrollment)}
                    >
                      Start integration
                    </Button>
                    <Button
                      size="small"
                      component={RouterLink}
                      to={`/product/${enrollment.product.uuid}`}
                    >
                      View product
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {tabIndex === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    label="Language"
                    value={codeLanguage}
                    onChange={(e) => setCodeLanguage(e.target.value)}
                  >
                    {CODE_LANGUAGES.map((lang) => (
                      <MenuItem key={lang} value={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload file
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".js,.ts,.jsx,.tsx,.py,.java,.go,.json,.yaml,.yml,.txt,.md,.sh"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleCodeFile(file);
                    e.target.value = "";
                  }}
                />
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={16}
                placeholder="Paste your integration code here…"
                value={codeText}
                onChange={(e) => setCodeText(e.target.value)}
                InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
              />
              {codeFileError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {codeFileError}
                </Alert>
              )}
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                disabled={reviewLoading || chat.loading || !codeText.trim()}
                onClick={() => void handleSubmitCodeReview()}
              >
                {reviewLoading ? "Reviewing…" : "Submit for review"}
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            {codeReview ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">Latest review</Typography>
                  <Chip
                    size="small"
                    label={overallStatusLabel(codeReview.overallStatus)}
                    color={
                      codeReview.overallStatus === "ready"
                        ? "success"
                        : codeReview.overallStatus === "blocked"
                          ? "error"
                          : "warning"
                    }
                  />
                </Stack>
                {codeReview.findings.map((f, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                    <strong>[{f.severity}]</strong> {f.issue}
                  </Typography>
                ))}
                <Button size="small" onClick={() => goToTabSlug("integrate")}>
                  View full review in chat
                </Button>
              </Paper>
            ) : (
              <Alert severity="info">
                Submit code for review. Results appear here and in the Integrate chat tab.
              </Alert>
            )}
          </Grid>
        </Grid>
      )}

      {tabIndex === 3 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Run a self-assessment certification readiness check against your focused product&apos;s
            developer guides and API specifications.
          </Typography>
          {!selectedEnrollment?.product?.uuid ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Select a product from the My products tab first.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Product: <strong>{selectedEnrollment.product.name}</strong>
            </Typography>
          )}
          <Button
            variant="contained"
            disabled={certifyLoading || chat.loading || !selectedEnrollment?.product?.uuid}
            onClick={() => void handleRunCertification()}
          >
            {certifyLoading ? "Assessing…" : "Run certification checklist"}
          </Button>

          {certification && (
            <Box sx={{ mt: 3, display: { xs: "block", md: "none" } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Checklist results</Typography>
                <Chip
                  label={readinessLabel(certification.overallReadiness)}
                  color={
                    certification.overallReadiness === "ready"
                      ? "success"
                      : certification.overallReadiness === "blocked"
                        ? "error"
                        : "warning"
                  }
                />
              </Stack>
              <Stack spacing={1}>
                {certification.items.map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={item.status.toUpperCase()}
                        color={
                          item.status === "pass"
                            ? "success"
                            : item.status === "fail"
                              ? "error"
                              : "default"
                        }
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {item.title}
                      </Typography>
                    </Stack>
                    {item.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {item.notes}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: { xs: "none", md: "block" } }}>
            {certification ? (
              <Paper variant="outlined" sx={{ p: 2, position: "sticky", top: 24 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Checklist results</Typography>
                  <Chip
                    label={readinessLabel(certification.overallReadiness)}
                    color={
                      certification.overallReadiness === "ready"
                        ? "success"
                        : certification.overallReadiness === "blocked"
                          ? "error"
                          : "warning"
                    }
                  />
                  {certification.passCount != null && (
                    <Typography variant="caption" color="text.secondary">
                      {certification.passCount} pass / {certification.failCount ?? 0} fail
                    </Typography>
                  )}
                </Stack>
                <Stack spacing={1}>
                  {certification.items.map((item) => (
                    <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          label={item.status.toUpperCase()}
                          color={
                            item.status === "pass"
                              ? "success"
                              : item.status === "fail"
                                ? "error"
                                : "default"
                          }
                        />
                        <Typography variant="body2" fontWeight={600}>
                          {item.title}
                        </Typography>
                      </Stack>
                      {item.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {item.notes}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            ) : (
              <Alert severity="info" sx={{ position: "sticky", top: 24 }}>
                Run the certification checklist to see readiness results here.
              </Alert>
            )}
          </Grid>
        </Grid>
      )}

      {tabIndex === 4 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Share feedback to help us improve the Integration Support Analyst. Your submission
            creates a support case for our team to review.
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography component="legend" variant="body2" sx={{ mb: 0.5 }}>
                Rating
              </Typography>
              <Rating
                value={feedbackRating}
                onChange={(_, v) => setFeedbackRating(v)}
              />
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={feedbackCategory}
                onChange={(e) => setFeedbackCategory(e.target.value)}
              >
                {FEEDBACK_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Your feedback"
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeTranscript}
                  onChange={(e) => setIncludeTranscript(e.target.checked)}
                />
              }
              label="Include recent chat excerpt"
            />
            {feedbackError && <Alert severity="error">{feedbackError}</Alert>}
            {feedbackSuccess && <Alert severity="success">{feedbackSuccess}</Alert>}
            <Button
              variant="contained"
              disabled={feedbackSubmitting}
              onClick={() => void handleSubmitFeedback()}
            >
              {feedbackSubmitting ? "Submitting…" : "Submit feedback"}
            </Button>
          </Stack>
        </Paper>
      )}
    </HubPageShell>
  );
};

export default IntegrationSupportHubPage;
